import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToStepsSchema,
  buildSpeakableSchema,
} from "@/lib/schema";
import { CONSULAR_FAQ_CATEGORIES } from "@/content/consular-corpus/faq-defs";
import { CONSULAR_DOC_LANDINGS } from "@/content/consular-corpus/documents";
import { CONSULAR_COUNTRY_NAMES as CONSULAR_COUNTRY_LANDINGS } from "@/content/consular-corpus/names.gen";

const STEPS = [
  {
    title: "Check the original and its issuing authority",
    body: "Send photos of the originals so we can confirm the Department of Consular Affairs will accept them, and whether an upstream certification (district office, Department of Provincial Administration, university registrar) is required first.",
  },
  {
    title: "Prepare the certified translation the destination requires",
    body: "We translate into English or the destination language in the format the receiving authority expects, with the translator's certification the MFA relies on when it stamps the file.",
  },
  {
    title: "File for MFA legalisation",
    body: "Filed at the Legalisation Division on Chaeng Watthana Road: THB 200 per stamp for the normal 2–3 working-day track, or THB 400 per stamp for same-day express when lodged before 11:30.",
  },
  {
    title: "Add embassy legalisation or use an Apostille",
    body: "Before 28 February 2027 most files still need the destination embassy in Thailand. Once the Hague Convention enters into force for Thailand, a single Apostille replaces the embassy step for member states.",
  },
  {
    title: "Delivery and digital record",
    body: "You receive the completed set plus scans of every stamp, so you can answer follow-up requests from the receiving authority without re-filing.",
  },
];

export const Route = createFileRoute("/en/consular-legalization/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/consular-corpus/faq");
    return { faqs: m.CONSULAR_FAQ_EN.slice(0, 12) };
  },
  component: EnConsularHub,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const faqs = loaderData?.faqs ?? [];
    return {
      ...pageHeadForProvince({
        title: "Thai MFA Legalisation — Steps, Fees and Destination Countries",
        description:
          "Consular legalisation of Thai documents: choose by document type or destination country, see the correct certification chain, official fees, turnaround, and the Apostille route that starts for Thailand in 2027.",
        path: "/en/consular-legalization",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/consular-legalization" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "MFA Legalisation", url: `${site}/en/consular-legalization` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildHowToStepsSchema({
              name: "How to legalise Thai documents for use abroad",
              description: "The real working sequence from checking originals to destination embassy legalisation.",
              path: "/en/consular-legalization",
              inLanguage: "en",
              steps: STEPS.map((s, i) => ({ step: i + 1, title: s.title, body: s.body })),
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "MFA legalisation by destination country",
            itemListElement: CONSULAR_COUNTRY_LANDINGS.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/en/consular-legalization/country/${c.slug}`,
              name: c.nameEn,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/consular-legalization")) },
      ],
    };
  },
});

function EnConsularHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>MFA Legalisation</span>
        </nav>
        <h1 className="text-3xl font-extrabold md:text-4xl">
          Thai MFA Legalisation: choose by document type or destination country
        </h1>
        <p className="mt-4 max-w-3xl text-muted-foreground" data-speakable="true">
          Legalisation is the step where the Legalisation Division of the Department of Consular Affairs certifies the
          signature and the translation so a Thai document is accepted abroad. Official fees are THB 200 per stamp on the
          normal 2–3 working-day track and THB 400 per stamp for same-day express filed before 11:30. Until 28 February
          2027 most files also need the destination embassy in Thailand.
        </p>

        <section className="mt-10">
          <h2 className="mb-4 text-xl font-bold">The five working steps</h2>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.title} id={`step-${i + 1}`} className="rounded-lg border bg-card p-4">
                <div className="font-semibold">{i + 1}. {s.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Knowledge topics and FAQs</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {CONSULAR_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/consular-legalization/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
          <a href="/en/consular-legalization/faq" className="mt-4 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">
            Browse every question and answer
          </a>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Legalisation by document type</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_DOC_LANDINGS.map((d) => (
              <a key={d.slug} href={`/en/consular-legalization/document/${d.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {d.titleEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Legalisation by destination country</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_COUNTRY_LANDINGS.map((c) => (
              <a key={c.slug} href={`/en/consular-legalization/country/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.nameEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-bold">Frequently asked questions</h2>
          {faqs.map((f: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 4 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Have us review the file before you lodge it</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the destination country, the receiving authority and your deadline — free document check by phone,
            LINE or email.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact our team</a>
        </section>
        <FaqSourceNote topic="consular" locale="en" />
      </main>
      <SiteFooter />
    </div>
  );
}